/**
 * Discriminated-union graph node types.
 *
 * Mirrors jnana-native's per-type modeling:
 *   - One TS interface per nodeTypeId; each carries a literal `nodeTypeId`
 *     so TS narrowing works (`if (n.nodeTypeId === NodeTypeId.TALAM) ...`).
 *   - BhajanNode handles bhajan / stotra / mantra via a sub-discriminator
 *     `bhajanType` (matching native's BhajanType enum) — they all share
 *     `nodeTypeId === BHAJAN`, so they can't be separate union members.
 *   - GenericGraphNode is the catch-all for nodeTypeIds we don't yet have
 *     specific types for. It uses Record<string, unknown> so consumers
 *     must narrow before reading data fields.
 *
 * Edges are kept in the canonical jnana-web shape: Array<{name, nodeId}>.
 * (jnana-admin used a Record<string, number|number[]> map; we normalize
 * at the edge-filter layer in src/filters/definitions/edgeFilter.ts.)
 */

import { NodeTypeId } from './graph.types';
import { getDisplayLanguage } from '../utils/displayLanguage';

export { NodeTypeId } from './graph.types';

/** Sub-discriminator for nodeTypeId === BHAJAN. Matches native BhajanType. */
enum BhajanType {
  Bhajan = 0,
  Stotra = 1,
  Mantra = 2,
}

export interface NodeEdge {
  name: string;
  nodeId: number;
}

interface BaseNode {
  nodeId: number;
  edges: NodeEdge[];
  /** Some payloads include `domain` (singular) or `domains` (numeric mask). */
  domain?: number;
  domains?: number;
  /** Numeric ms-epoch timestamps in the API. */
  created?: number;
  updated?: number;
  /**
   * Server-stamped boolean flag: this bhajan's lyrics carry svara notation.
   * Derived by the slim catalog index from the LYRICS node, which the index
   * itself drops — so the client cannot compute it and must be told.
   * Absent on every node until that index change deploys.
   */
  hasSvaras?: boolean;
}

/* ============================================================
 *  Typed nodes — each binds a literal nodeTypeId + typed data
 * ============================================================ */

interface BhajanNode extends BaseNode {
  nodeTypeId: NodeTypeId.BHAJAN;
  data: {
    name?: string;
    normalizedName?: string;
    bhajanType?: BhajanType;
    teName?: string;
    enName?: string;
    [k: string]: unknown;
  };
}

interface LyricsNode extends BaseNode {
  nodeTypeId: NodeTypeId.LYRICS;
  data: {
    text?: string;
    [k: string]: unknown;
  };
}

interface RagamNode extends BaseNode {
  nodeTypeId: NodeTypeId.RAGAM;
  data: {
    name?: string;
    normalizedName?: string;
    aroha?: string;
    avaroha?: string;
    [k: string]: unknown;
  };
}

interface TalamNode extends BaseNode {
  nodeTypeId: NodeTypeId.TALAM;
  data: {
    name?: string;
    normalizedName?: string;
    /** e.g. "| , , | , , | , , | , , |" — drives svara group alignment. */
    beatPattern?: string;
    nadaCount?: number;
    [k: string]: unknown;
  };
}

interface ComposerNode extends BaseNode {
  nodeTypeId: NodeTypeId.COMPOSER;
  data: {
    name?: string;
    normalizedName?: string;
    [k: string]: unknown;
  };
}

interface DeityNode extends BaseNode {
  nodeTypeId: NodeTypeId.DEITY;
  data: {
    name?: string;
    normalizedName?: string;
    [k: string]: unknown;
  };
}

interface AlbumNode extends BaseNode {
  nodeTypeId: NodeTypeId.ALBUM;
  data: {
    name?: string;
    artUrl60?: string;
    artUrl100?: string;
    /** Apple Music album/collection id. */
    collectionId?: string;
    spotifyAlbumId?: string;
    audioType?: string;
    [k: string]: unknown;
  };
}

interface TrackNode extends BaseNode {
  nodeTypeId: NodeTypeId.TRACK;
  data: {
    name?: string;
    discNum?: string;
    /** Apple Music track id. */
    trackId?: string;
    trackNum?: string;
    /** 30s preview m4a URL. */
    previewUrl?: string;
    spotifyTrackId?: string;
    /** stringified ms duration. */
    trackDurationMillis?: string;
    [k: string]: unknown;
  };
}

interface AudibleNode extends BaseNode {
  nodeTypeId: NodeTypeId.AUDIBLE;
  data: {
    /** Per-line ordering markers (svara line indices). */
    orderIds?: string;
    /** Per-line timing offsets (ms). */
    timingIds?: string;
    [k: string]: unknown;
  };
}

interface MeaningNode extends BaseNode {
  nodeTypeId: NodeTypeId.MEANING;
  data: {
    text?: string;
    [k: string]: unknown;
  };
}

interface CompositionLanguageNode extends BaseNode {
  nodeTypeId: NodeTypeId.COMPOSITION_LANGUAGE;
  data: {
    name?: string;
    normalizedName?: string;
    [k: string]: unknown;
  };
}

interface BhajanBabiesNode extends BaseNode {
  nodeTypeId: NodeTypeId.BHAJAN_BABIES;
  data: {
    /** Native used `youtubeLink`; the public API returns `ytLink`. Both supported. */
    ytLink?: string;
    youtubeLink?: string;
    [k: string]: unknown;
  };
}

interface CollectionNode extends BaseNode {
  nodeTypeId: NodeTypeId.COLLECTION;
  data: {
    name?: string;
    normalizedName?: string;
    /** Cover image URL for the collection. */
    artUrl?: string;
    [k: string]: unknown;
  };
}

/** Catch-all for any nodeTypeId we haven't given a precise type yet. */
interface GenericGraphNode extends BaseNode {
  nodeTypeId: Exclude<
    NodeTypeId,
    | NodeTypeId.BHAJAN
    | NodeTypeId.LYRICS
    | NodeTypeId.RAGAM
    | NodeTypeId.TALAM
    | NodeTypeId.COMPOSER
    | NodeTypeId.DEITY
    | NodeTypeId.ALBUM
    | NodeTypeId.TRACK
    | NodeTypeId.AUDIBLE
    | NodeTypeId.MEANING
    | NodeTypeId.COMPOSITION_LANGUAGE
    | NodeTypeId.BHAJAN_BABIES
    | NodeTypeId.COLLECTION
  >;
  data: Record<string, unknown>;
}

export type AnyGraphNode =
  | BhajanNode
  | LyricsNode
  | RagamNode
  | TalamNode
  | ComposerNode
  | DeityNode
  | AlbumNode
  | TrackNode
  | AudibleNode
  | MeaningNode
  | CompositionLanguageNode
  | BhajanBabiesNode
  | CollectionNode
  | GenericGraphNode;

/* ============================================================
 *  Helpers
 * ============================================================ */

/** Find the FIRST edge by name on a node; returns undefined if none. */
export function getEdge(node: AnyGraphNode, edgeName: string): NodeEdge | undefined {
  return node.edges?.find((e) => e.name === edgeName);
}

/**
 * Resolve an edge to its target node within a node list, optionally type-guarded.
 * Returns undefined when the edge or target is missing, or when the type guard fails.
 *
 *   const talam = findEdgeNode<TalamNode>(bhajan, 'talam', allNodes);
 *   talam?.data.beatPattern  // string | undefined, fully narrowed
 */
export function findEdgeNode<T extends AnyGraphNode = AnyGraphNode>(
  source: AnyGraphNode,
  edgeName: string,
  all: AnyGraphNode[],
  guard?: (n: AnyGraphNode) => n is T
): T | undefined {
  const e = getEdge(source, edgeName);
  if (!e) return undefined;
  const target = all.find((n) => n.nodeId === e.nodeId);
  if (!target) return undefined;
  if (guard) return guard(target) ? target : undefined;
  return target as T;
}

/** Display name resolver — prefer normalizedName, fall back to name. */
export function nodeName(
  node: AnyGraphNode | undefined | null,
  language: string = getDisplayLanguage()
): string | undefined {
  if (!node) return undefined;
  const d = node.data as { name?: unknown; normalizedName?: unknown } | undefined;
  // A name that is empty or just "." is a placeholder for a missing value
  // (e.g. an unspecified ragam/talam) — treat it as absent.
  const clean = (v: unknown): string | undefined => {
    if (typeof v !== 'string') return undefined;
    const t = v.trim();
    return t && t !== '.' ? v : undefined;
  };
  const normalized = clean(d?.normalizedName);
  const native = clean(d?.name);
  // English shows the ASCII normalized name; other languages show the native script.
  if (language !== 'en') return native ?? normalized;
  return normalized ?? native;
}
